import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { candidateApi, errorMessage } from '@/lib/api';
import type {
  Application,
  ApplicationStatus,
  AtsScore,
  CandidateProfile,
  CandidateSummary,
  Paginated,
  PipelineAnalytics,
} from '@/types';

export interface ApplicationFilters {
  page: number;
  limit: number;
  jobId: string;
  status: string;
  search: string;
  minAtsScore: string;
  sortBy: 'createdAt' | 'atsScore' | 'candidateName' | 'status';
  sortDir: 'asc' | 'desc';
}

export interface CandidateState {
  applications: Application[];
  applicationPagination: Paginated<Application>['pagination'] | null;
  currentApplication: Application | null;
  currentAts: AtsScore | null;
  candidates: CandidateSummary[];
  candidatePagination: Paginated<CandidateSummary>['pagination'] | null;
  profile: CandidateProfile | null;
  analytics: PipelineAnalytics | null;
  filters: ApplicationFilters;
  loading: boolean;
  scoring: boolean;
  error: string | null;
}

const initialState: CandidateState = {
  applications: [],
  applicationPagination: null,
  currentApplication: null,
  currentAts: null,
  candidates: [],
  candidatePagination: null,
  profile: null,
  analytics: null,
  filters: {
    page: 1,
    limit: 20,
    jobId: '',
    status: '',
    search: '',
    minAtsScore: '',
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
  loading: false,
  scoring: false,
  error: null,
};

function toParams(filters: ApplicationFilters): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value != null)
  ) as Record<string, string | number>;
}

export const fetchApplications = createAsyncThunk<
  Paginated<Application>,
  Partial<ApplicationFilters> | void,
  { rejectValue: string }
>('candidates/fetchApplications', async (overrides, { getState, rejectWithValue }) => {
  const state = (getState() as { candidates: CandidateState }).candidates;
  try {
    const { data } = await candidateApi.get<Paginated<Application>>('/company/applications', {
      params: toParams({ ...state.filters, ...(overrides ?? {}) }),
    });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not load applications'));
  }
});

export const fetchApplication = createAsyncThunk<Application, string, { rejectValue: string }>(
  'candidates/fetchApplication',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await candidateApi.get<Application>(`/applications/${id}`);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load this application'));
    }
  }
);

export const fetchAtsScore = createAsyncThunk<AtsScore | null, string, { rejectValue: string }>(
  'candidates/fetchAts',
  async (applicationId, { rejectWithValue }) => {
    try {
      const { data } = await candidateApi.get<AtsScore>(`/ats-scores/${applicationId}`);
      return data;
    } catch (err) {
      // Not scored yet is a normal state, not an error worth surfacing.
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      return rejectWithValue(errorMessage(err, 'Could not load the ATS analysis'));
    }
  }
);

export const updateApplicationStatus = createAsyncThunk<
  Application,
  { id: string; status: ApplicationStatus; notes?: string },
  { rejectValue: string }
>('candidates/updateStatus', async ({ id, status, notes }, { rejectWithValue }) => {
  try {
    const { data } = await candidateApi.patch<Application>(`/applications/${id}/status`, { status, notes });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not update the status'));
  }
});

export const calculateAts = createAsyncThunk<Application, string, { rejectValue: string }>(
  'candidates/calculateAts',
  async (applicationId, { rejectWithValue }) => {
    try {
      const { data } = await candidateApi.post<Application>(`/applications/${applicationId}/calculate-ats`);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not score this resume'));
    }
  }
);

export const fetchAnalytics = createAsyncThunk<PipelineAnalytics, string | undefined, { rejectValue: string }>(
  'candidates/analytics',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await candidateApi.get<PipelineAnalytics>('/applications/analytics', {
        params: jobId ? { jobId } : undefined,
      });
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load pipeline analytics'));
    }
  }
);

export const fetchCandidates = createAsyncThunk<
  Paginated<CandidateSummary>,
  { page?: number; limit?: number; search?: string } | void,
  { rejectValue: string }
>('candidates/fetchCandidates', async (params, { rejectWithValue }) => {
  try {
    const { data } = await candidateApi.get<Paginated<CandidateSummary>>('/company/candidates', {
      params: params ?? undefined,
    });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not load candidates'));
  }
});

export const fetchCandidateProfile = createAsyncThunk<CandidateProfile, string, { rejectValue: string }>(
  'candidates/fetchProfile',
  async (candidateId, { rejectWithValue }) => {
    try {
      const { data } = await candidateApi.get<CandidateProfile>(`/company/candidates/${candidateId}`);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load this candidate'));
    }
  }
);

const candidateSlice = createSlice({
  name: 'candidates',
  initialState,
  reducers: {
    setFilters(state, action: { payload: Partial<ApplicationFilters> }) {
      const resetPage = Object.keys(action.payload).some((key) => key !== 'page');
      state.filters = { ...state.filters, ...action.payload, ...(resetPage ? { page: 1 } : {}) };
    },
    clearCurrent(state) {
      state.currentApplication = null;
      state.currentAts = null;
    },
  },
  extraReducers: (builder) => {
    const replaceInList = (state: CandidateState, application: Application) => {
      const index = state.applications.findIndex((a) => a._id === application._id);
      if (index >= 0) state.applications[index] = application;
      if (state.currentApplication?._id === application._id) state.currentApplication = application;
    };

    builder
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.data;
        state.applicationPagination = action.payload.pagination;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchApplication.fulfilled, (state, action) => {
        state.currentApplication = action.payload;
      })
      .addCase(fetchAtsScore.fulfilled, (state, action) => {
        state.currentAts = action.payload;
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        replaceInList(state, action.payload);
      })
      .addCase(calculateAts.pending, (state) => {
        state.scoring = true;
      })
      .addCase(calculateAts.fulfilled, (state, action) => {
        state.scoring = false;
        replaceInList(state, action.payload);
      })
      .addCase(calculateAts.rejected, (state, action) => {
        state.scoring = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      })
      .addCase(fetchCandidates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.loading = false;
        state.candidates = action.payload.data;
        state.candidatePagination = action.payload.pagination;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchCandidateProfile.pending, (state) => {
        state.loading = true;
        state.profile = null;
      })
      .addCase(fetchCandidateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchCandidateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { setFilters, clearCurrent } = candidateSlice.actions;
export default candidateSlice.reducer;
